<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { ref, computed, useId } from "vue"
import { useI18n } from "vue-i18n"
import { useConfirm } from "primevue"
import { useRedirect } from "@core/composables"
import { useSourceStore } from "@core/stores/source.store"
import { useAuthStore } from "@core/stores/auth.store"
import { formatDate } from "@core/helpers"
import type { ColumnConfig, Source } from "@types"
import type { SensorPositionFormValue } from "@core/components/source/sensor/position/SensorPositionForm.vue"

// ================================
// PROPS & EMITS
// ================================
interface Props {
    /**
     * Identifiant de la source
     */
    sourceId: string
}

const props = defineProps<Props>()
const { redirectWrapper } = useRedirect()

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            title: "Historique des positions du capteur",
            empty_message: "Ce capteur n'est rattaché à aucun bâtiment",
            single_position_message:
                "Pour supprimer la position de la source, veuillez ajouter une nouvelle position puis supprimer l'ancienne.",
            add_position: "Ajouter une position",
            building: "Bâtiment",
            start_date: "Date d'installation",
            end_date: "Date de désinstallation",
            description: "Description",
            floor: "Étage",
            actions: "Actions",
            edit: "Éditer",
            delete: "Supprimer",
            form: {
                add_position: "Ajouter une position",
                edit_position: "Modifier une position",
            },
            delete_confirm: {
                header: "Supprimer la position",
                message:
                    "Êtes-vous sûr de vouloir supprimer cette position du capteur ?",
                accept: "Supprimer",
                reject: "Annuler",
            },
            delete_error_message:
                "La suppression n'a pas pu être effectuée. La liste a été rétablie. Veuillez réessayer ou contacter notre support si le problème persiste.",
        },
    },
})

// ================================
// COMPOSABLES & STORES
// ================================
const confirm = useConfirm()
const componentId = useId()
const sourceStore = useSourceStore()
const authStore = useAuthStore()

// ================================
// ÉTAT DES DONNÉES
// ================================
/**
 * Indicateur de visibilité de la modal du formulaire
 */
const isModalVisible = ref<boolean>(false)

/**
 * Valeur du formulaire (v-model)
 */
const formValue = ref<SensorPositionFormValue | null>(null)

/**
 * Afficher le message d'erreur de suppression (rollback)
 */
const deleteErrorShown = ref<boolean>(false)

// ================================
// DONNÉES CALCULÉES
// ================================
/**
 * Récupère la source depuis le store
 */
const source = computed<Source | undefined>(() => {
    return sourceStore.getSourceById(props.sourceId)
})

/**
 * Liste des positions (buildings) de la source
 */
const positions = computed(() => {
    return source.value?.buildings || []
})

/**
 * Vérifie si l'utilisateur a les permissions pour ajouter/éditer
 */
const isCanEdit = computed((): boolean => {
    return authStore.hasOfferEnergyManager && authStore.isEmployeWriteOrHigher
})

/**
 * Vérifie s'il n'y a qu'une seule position
 */
const hasSinglePosition = computed((): boolean => {
    return positions.value.length === 1
})

/**
 * Afficher le bouton supprimer (plus d'une position et permissions)
 */
const isCanDelete = computed(
    (): boolean => !!isCanEdit.value && !hasSinglePosition.value
)

// ================================
// CONFIGURATION DU TABLEAU
// ================================
/**
 * Configuration des colonnes du tableau (sans tri, largeurs en rem)
 */
const columns = computed<ColumnConfig[]>(() => {
    const columns = [
        {
            field: "building",
            header: t("building"),
            sortable: false,
            style: { width: "14rem" } as Record<string, string>,
        },
        {
            field: "start_date",
            header: t("start_date"),
            sortable: false,
            style: { width: "7rem" } as Record<string, string>,
        },
        {
            field: "end_date",
            header: t("end_date"),
            sortable: false,
            style: { width: "7rem" } as Record<string, string>,
        },
        {
            field: "description",
            header: t("description"),
            sortable: false,
            style: { width: "9.375rem" } as Record<string, string>,
        },
        {
            field: "floor",
            header: t("floor"),
            sortable: false,
            style: { width: "6.25rem" } as Record<string, string>,
        },
    ]

    if (isCanEdit.value || isCanDelete.value) {
        columns.push({
            field: "actions",
            header: t("actions"),
            sortable: false,
            style: { width: "6.25rem" } as Record<string, string>,
        })
    }

    return columns
})

/**
 * Données formatées pour le tableau
 */
const tableData = computed(() => {
    return positions.value.map((building) => ({
        _id: building._id,
        building: formatBuildingLabel(building),
        start_date: building.start_date
            ? formatDate(building.start_date, "DD/MM/YYYY")
            : "-",
        end_date: building.end_date
            ? formatDate(building.end_date, "DD/MM/YYYY")
            : "-",
        description: building.description || "-",
        floor: building.floor || "-",
        actions: building._id,
        // Données brutes pour l'édition
        raw: building,
    }))
})

// ================================
// LOGIQUE MÉTIER
// ================================
/**
 * Formate l'affichage d'un bâtiment : "Nom - Adresse"
 * @param building - Bâtiment à formater
 * @returns Chaîne formatée
 */
function formatBuildingLabel(building: {
    name?: string
    address?: {
        street?: string
        city?: string
        zipcode?: string
    }
}): string {
    const name = building.name || ""
    const addressParts = building.address
        ? [
              building.address.street,
              building.address.city,
              building.address.zipcode,
          ]
              .filter(Boolean)
              .join(", ")
        : ""

    if (addressParts) {
        return `${name} - ${addressParts}`
    }
    return name || "-"
}

/**
 * Bâtiment normalisé pour ItemBuilding (niveau depuis infos si besoin)
 */
function buildingForItem(raw: (typeof positions.value)[0]) {
    return {
        _id: raw._id,
        name: raw.name,
        niveau: raw.niveau ?? (raw as any).infos?.niveau,
        address: raw.address,
    }
}

// ================================
// INTERACTIONS UTILISATEUR
// ================================
/**
 * Gère le clic sur le bouton "Ajouter une position"
 */
function onClickAddPosition(): void {
    formValue.value = null
    deleteErrorShown.value = false
    isModalVisible.value = true
}

/**
 * Gère le clic sur le bouton "Éditer" d'une position
 * @param building - Bâtiment à éditer
 */
function onClickEdit(building: {
    _id: string
    name?: string
    start_date?: string
    end_date?: string
    description?: string
    floor?: string
}): void {
    formValue.value = {
        _id_building: building._id,
        buildingName: building.name,
        start_date: building.start_date,
        end_date: building.end_date,
        description: building.description,
        floor: building.floor,
    }
    isModalVisible.value = true
}

/**
 * Gère la mise à jour du formulaire (v-model)
 * @param value - Valeur du formulaire
 */
async function onFormUpdate(
    value: SensorPositionFormValue | null
): Promise<void> {
    if (!value) return

    isModalVisible.value = false
    formValue.value = null
    deleteErrorShown.value = false
}

/**
 * Gère l'annulation du formulaire
 */
function onFormCancel(): void {
    isModalVisible.value = false
    formValue.value = null
}

/**
 * Gère le clic sur une ligne du tableau pour naviguer vers le bâtiment
 * @param buildingId - Identifiant du bâtiment
 */
function onClickBuilding(buildingId: string): void {
    redirectWrapper("/building/:_id_building/data", {
        _id_building: buildingId,
    })
}

/**
 * Demande confirmation puis supprime une position (optimistic + rollback si erreur)
 * @param building - Bâtiment à supprimer
 * @returns void
 */
function requestDeletePosition(building: { _id: string; name?: string }): void {
    confirm.require({
        group: componentId,
        header: t("delete_confirm.header"),
        message: t("delete_confirm.message"),
        icon: "pi pi-info-circle",
        rejectLabel: t("delete_confirm.reject"),
        rejectProps: {
            label: t("delete_confirm.reject"),
            outlined: true,
            severity: "secondary",
        },
        acceptProps: {
            label: t("delete_confirm.accept"),
            icon: "pi pi-trash",
            severity: "danger",
        },
        accept: async () => {
            const buildingId = building._id

            deleteErrorShown.value = false

            const {
                fetch: fetchDeletePosition,
                hasError: hasErrorDeletingPosition,
            } = sourceStore.getRemoveBuildingFromSourceFetcher(
                buildingId,
                props.sourceId
            )

            await fetchDeletePosition()
            deleteErrorShown.value = hasErrorDeletingPosition.value
        },
    })
}
</script>

<template>
    <BaseTitledCard class="sensor-position-history">
        <template #header>
            {{ t("title") }}
        </template>
        <!-- ================================ -->
        <!-- CONTENU PRINCIPAL -->
        <!-- ================================ -->
        <template #content>
            <div class="space-y-6">
                <!-- Tableau des positions -->
                <BaseTable
                    data-key="_id"
                    :columns="columns"
                    :value="tableData"
                    size="small"
                    :empty-message="t('empty_message')"
                >
                    <template #cell_building="{ data }">
                        <ItemBuilding
                            :building="buildingForItem(data.raw)"
                            @click="onClickBuilding"
                        />
                    </template>

                    <template #cell_actions="{ data }">
                        <div class="flex justify-center gap-2">
                            <BaseButton
                                v-if="isCanEdit"
                                :icon="'pi pi-pencil'"
                                rounded
                                text
                                :aria-label="t('edit')"
                                v-tooltip.top="t('edit')"
                                @click="onClickEdit(data.raw)"
                            />
                            <BaseButton
                                v-if="isCanDelete"
                                :icon="'pi pi-trash'"
                                rounded
                                text
                                :aria-label="t('delete')"
                                v-tooltip.top="t('delete')"
                                @click="requestDeletePosition(data.raw)"
                            />
                        </div>
                    </template>
                </BaseTable>

                <!-- Message si une seule position -->
                <div
                    v-if="hasSinglePosition"
                    class="bg-blue-50 border border-blue-200 rounded p-4 text-sm text-blue-800"
                >
                    <p>{{ t("single_position_message") }}</p>
                </div>

                <!-- Bouton ajouter une position -->
                <div v-if="isCanEdit" class="flex flex-col items-center gap-2">
                    <BaseButton
                        :label="t('add_position')"
                        :icon="'pi pi-plus'"
                        icon-pos="right"
                        @click="onClickAddPosition"
                    />
                    <!-- Erreur suppression (rollback) -->
                    <BaseMessage
                        v-if="deleteErrorShown"
                        severity="error"
                        class="w-full mx-auto"
                    >
                        <p>{{ t("delete_error_message") }}</p>
                    </BaseMessage>
                </div>
            </div>

            <!-- ================================ -->
            <!-- MODAL FORMULAIRE D'AJOUT/ÉDITION -->
            <!-- ================================ -->
            <BaseDialog
                v-model:visible="isModalVisible"
                modal
                :closable="true"
                dismissable-mask
                :style="{ maxWidth: '40rem', width: '100%' }"
                :breakpoints="{ '1199px': '90vw' }"
                :header="
                    formValue?._id_building
                        ? t('form.edit_position')
                        : t('form.add_position')
                "
            >
                <SensorPositionForm
                    :source-id="sourceId"
                    :mode="formValue?._id_building ? 'update' : 'add'"
                    v-model="formValue"
                    @update:model-value="onFormUpdate"
                    @cancel="onFormCancel"
                />
            </BaseDialog>

            <BaseConfirmDialog :group="componentId" />
        </template>
    </BaseTitledCard>
</template>
